import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronDown, ChevronUp, Lightbulb, X, Save, Sparkles } from 'lucide-react';
import StepProgress from './StepProgress';

const WritingPanel = ({
  template,
  currentStep,
  currentStepIndex = 0,
  totalSteps = 0,
  response = '',
  onResponseChange,
  onContinue,
  onBack,
  onSave,
  onWeave,
  wordGoal,
  isLastStep = false,
  isSaving = false,
  totalWordCount = 0,
  allSectionsHaveContent = false
}) => {
  const textareaRef = useRef(null);
  const [wordCount, setWordCount] = useState(0);
  const [canContinue, setCanContinue] = useState(false);
  const [showCoachHint, setShowCoachHint] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const inactivityTimerRef = useRef(null);

  // Calculate word count
  useEffect(() => {
    const words = response.trim() ? response.trim().split(/\s+/).length : 0;
    setWordCount(words);
    
    // Check if user has written enough to continue
    const minWords = currentStep?.minWords || 80;
    const unlockWords = currentStep?.unlockWords || Math.ceil(minWords * 0.5);
    setCanContinue(words >= unlockWords);
  }, [response, currentStep]);

  // Auto-focus textarea when step changes
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
    // Reset coach hint visibility when changing steps
    setShowCoachHint(false);
    setHasInteracted(false);
  }, [currentStepIndex]);

  // Inactivity timer for auto-showing coach hint
  useEffect(() => {
    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Only set timer if hint is not already shown and user hasn't manually toggled it
    if (!showCoachHint && !hasInteracted && currentStep?.coachVoice) {
      inactivityTimerRef.current = setTimeout(() => {
        setShowCoachHint(true);
      }, 20000); // 20 seconds
    }

    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [showCoachHint, hasInteracted, response, currentStep]);

  // Auto-resize textarea
  const handleTextareaResize = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.max(240, textarea.scrollHeight)}px`;
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    onResponseChange(value);
    handleTextareaResize();
    
    // Reset inactivity timer on user input
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    // Set new inactivity timer
    if (!showCoachHint && !hasInteracted && currentStep?.coachVoice) {
      inactivityTimerRef.current = setTimeout(() => {
        setShowCoachHint(true);
      }, 20000);
    }
  };

  const toggleCoachHint = () => {
    setShowCoachHint(!showCoachHint);
    setHasInteracted(true); // Mark that user has manually interacted with the hint
  };

  const minWords = currentStep?.minWords || 80;
  const hasEnoughWords = wordCount >= minWords;
  const progress = Math.min((wordCount / minWords) * 100, 100);
  
  // Check if can weave (all sections have content and 50% of word goal)
  const canWeave = allSectionsHaveContent && totalWordCount >= (650 * 0.5);

  return (
    <div className="writing-panel">
      {/* Header */}
      <div className="writing-header">
        <button className="back-btn" onClick={onBack}>
          <ChevronLeft className="w-4 h-4" />
          <span>Change template</span>
        </button>
        
        <StepProgress 
          currentStep={currentStepIndex}
          totalSteps={totalSteps}
        />
      </div>

      {/* Main Content */}
      <div className="writing-content">
        <h2 className="step-title">{currentStep?.label}</h2>
        <p className="step-prompt">{currentStep?.prompt}</p>
        
        {currentStep?.coachVoice && (
          <div className="coach-hint-container">
            {!showCoachHint ? (
              <button 
                className="coach-hint-toggle"
                onClick={toggleCoachHint}
                aria-label="Show writing tips"
              >
                <Lightbulb className="w-4 h-4" />
                <span>Show writing tips</span>
                <ChevronDown className="w-4 h-4" />
              </button>
            ) : (
              <div className="coach-hint expanded">
                <div className="coach-hint-header">
                  <div className="coach-hint-title">
                    <Lightbulb className="w-4 h-4" />
                    <span>Coach's Tip</span>
                  </div>
                  <button 
                    className="coach-hint-close"
                    onClick={toggleCoachHint}
                    aria-label="Hide writing tips"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="coach-hint-content">
                  {currentStep.coachVoice}
                  {currentStep.sample && (
                    <div className="coach-hint-sample">
                      <div className="sample-label">Example:</div>
                      <div className="sample-text">"{currentStep.sample}"</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <textarea
          ref={textareaRef}
          className="writing-area"
          value={response}
          onChange={handleInputChange}
          placeholder={`Write your response here...`}
          onInput={handleTextareaResize}
        />

        <div className="writing-footer">
          <div className={`word-count ${hasEnoughWords ? 'good' : ''}`}>
            {wordCount}/{minWords} words
            {!hasEnoughWords && wordCount > 0 && (
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
          </div>
          
          <div className="footer-buttons">
            {/* Save Draft Button */}
            <button 
              className="save-btn"
              onClick={onSave}
              disabled={isSaving}
            >
              <Save className="w-4 h-4" />
              <span>{isSaving ? 'Saving...' : 'Save Draft'}</span>
            </button>
            
            {/* Continue or Weave Button */}
            {isLastStep ? (
              <button 
                className="weave-btn"
                onClick={onWeave}
                disabled={!canWeave}
                title={!canWeave ? 'Complete all sections and reach 50% word count' : 'Continue to refinement'}
              >
                <Sparkles className="w-4 h-4" />
                <span>Review & Refine</span>
              </button>
            ) : (
              <button 
                className="continue-btn"
                onClick={onContinue}
                disabled={!canContinue}
              >
                <span>Continue to Step {currentStepIndex + 2}</span>
                <ChevronLeft className="w-4 h-4 rotate-180" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WritingPanel;